/**
 * Copyright 2024 ElectricSQL and contributors.
 * Copyright 2026 Improve Labs.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * The leader-election MESSAGE VOCABULARY (`leader-here` / `tab-here` /
 * `rpc-call` / `rpc-return` / `rpc-error`, the per-tab BroadcastChannel, and the
 * tab-close Web Lock as a liveness signal) is derived from PGlite v0.5.4
 * (commit 6787212), `packages/pglite/src/worker/index.ts`. The implementation
 * below is original.
 *
 * DELIBERATE DIVERGENCES from PGlite, all of which show up in this vocabulary:
 *   - `leader-here` carries a `leaderEpoch` and is emitted ONLY after the
 *     resource is actually open (PGlite broadcasts before `init()` resolves).
 *   - `leader-failed` exists at all: PGlite has no leadership-failure path.
 *   - `rpc-call` is epoch-stamped so a message still sitting in a
 *     BroadcastChannel buffer across a handover cannot be executed by the NEW
 *     leader. That is what makes at-most-once real rather than aspirational.
 *
 * Type guards follow the shape of this repo's own deleted broker
 * (`git show a1abf2466e:packages/com/npmjs/expo-sqlite/web-sqlite-broker.web.ts`,
 * `parseBrokerMessage`), including its use of `isAnyObject`.
 */
import { isAnyObject } from "@-/com.npmjs.is-what";
import type { Cloneable, LeaderResourceEvent } from "#leader-resource-types";

/**
 * A serialized error. `structuredClone` DOES clone `Error` objects in modern
 * browsers, but it drops arbitrary own properties — including SQLite's `code`,
 * which `isDatabaseLockedError`'s `LOCKED_ERROR_CODES` check depends on. So
 * errors are serialized explicitly rather than relying on clone.
 */
export interface SerializedLeaderError {
  /** SQLite error code (e.g. `SQLITE_BUSY`). Load-bearing: see the note above. */
  code?: string;
  message: string;
  name: string;
  stack?: string;
}

// ── Main thread ↔ its OWN worker (`postMessage`, not broadcast) ────────────────

/** worker → tab: worker script evaluated; ready for options. */
export interface WorkerHereMessage {
  type: "worker-here";
}

/** tab → worker: configure and start the election. */
export interface HostInitMessage {
  /**
   * Opaque, structured-cloneable payload forwarded verbatim to the host's
   * `open(context)`. This protocol never inspects it — see
   * `LeaderResourceHostOptions.open` for why it is a message rather than a URL
   * parameter.
   */
  openContext?: Cloneable;
  resourceId: string;
  tabId: string;
  type: "host-init";
}

/** worker → tab: options accepted; election in progress. */
export interface HostReadyMessage {
  resourceId: string;
  type: "host-ready";
}

/** worker → tab: THIS worker won AND `open()` succeeded. */
export interface LeaderNowMessage {
  type: "leader-now";
}

/** worker → tab: this worker won but `precondition()`/`open()` failed; lock surrendered. */
export interface LeaderFailedDirectMessage {
  reason: string;
  /**
   * This worker will not try again: it exhausted its attempts, or a hung open or
   * close forced it to shut itself down. A tab that is still not connected to any
   * leader then fails `ready()` instead of waiting out its deadline. Absent on an
   * attempt that will be retried.
   */
  terminal?: boolean;
  type: "leader-failed-direct";
}

/**
 * tab → worker: proactively give up leadership (a `pagehide` with
 * `persisted === false`, or an explicit `close()`). OPTIMIZATION ONLY — the Web
 * Lock auto-release remains the sole correctness guarantee.
 */
export interface RelinquishMessage {
  type: "relinquish";
}

/**
 * worker → tab: the relinquish completed — `teardown()` ran and the resource's
 * `close()` resolved, so everything buffered is now durable on disk.
 *
 * This exists so an explicit `close()` can WAIT before terminating the worker.
 * Without it the tab posted `relinquish` and immediately called
 * `worker.terminate()`, which kills the host mid-`close()` — and for `guest.db`
 * that close is what folds the WAL back into the database file. The race lost
 * committed writes across a reload (onboarding completion reappearing as
 * incomplete; see the checkpoint note in `open-leader-turso-resource.web.ts`).
 *
 * Still only an OPTIMIZATION, exactly like the relinquish it answers: a tab that
 * dies without ever sending one is handled by the Web Lock auto-release, and the
 * waiter below is bounded so a worker that never replies cannot hang the close.
 */
export interface RelinquishedMessage {
  type: "relinquished";
}

export type LeaderWorkerMessage =
  | HostInitMessage
  | HostReadyMessage
  | LeaderFailedDirectMessage
  | LeaderNowMessage
  | RelinquishMessage
  | RelinquishedMessage
  | WorkerHereMessage;

// ── Resource-wide broadcast (`haniverse-leader-broadcast:${resourceId}`) ─────────────

/** tab → all: "I want a leader." Repeated on an interval until connected. */
export interface TabHereMessage {
  tabId: string;
  type: "tab-here";
}

/** leader → all: emitted ONLY after `open()` resolved. Seeing it means the resource is USABLE. */
export interface LeaderHereMessage {
  leaderEpoch: number;
  resourceId: string;
  type: "leader-here";
}

/** leader-candidate → all: the winner could not open. Informational; tabs stay in handover. */
export interface LeaderFailedMessage {
  leaderEpoch: number;
  reason: string;
  type: "leader-failed";
}

/** leader → all: app-level fan-out. */
export interface LeaderEventMessage {
  event: LeaderResourceEvent;
  type: "event";
}

/**
 * leader → all: the resource is TEMPORARILY closed; queue, and do NOT re-elect.
 *
 * Distinct from a handover in exactly one way, and it is the whole reason this
 * message exists: leadership did NOT change. The leader is deliberately holding
 * its election lock while the resource is closed, so that no other tab can elect
 * itself and re-open the underlying file during the window — which is what makes
 * the Turso sign-in wipe (delete `actor.db`, then reopen) safe with tabs open.
 *
 * Clients therefore treat it as handover MINUS re-election: queue new calls, but
 * do not restart the announce loop (there is nobody new to find) and do not
 * expect a fresh `leader-here`. The resumption signal is `leader-resumed`.
 *
 * The leader rejects its own in-flight calls before closing, so the client does
 * not need to (and must not — the epoch has not changed, so those calls belong to
 * the CURRENT leader and its own `rpc-error` responses will settle them).
 */
export interface LeaderPausedMessage {
  leaderEpoch: number;
  reason: string;
  type: "leader-paused";
}

/** leader → all: the resource is open again under the SAME leader; flush. */
export interface LeaderResumedMessage {
  leaderEpoch: number;
  type: "leader-resumed";
}

export type LeaderBroadcastMessage =
  | LeaderEventMessage
  | LeaderFailedMessage
  | LeaderHereMessage
  | LeaderPausedMessage
  | LeaderResumedMessage
  | TabHereMessage;

// ── Per-tab channel (`haniverse-leader-tab:${tabId}`) ────────────────────────────────

/** leader → tab: registered with this leader; flush the queue. */
export interface ConnectedMessage {
  leaderEpoch: number;
  /**
   * The leader's own end-to-end deadline per method (its wait in line plus its
   * run), when the resource declares one. The tab waits a little LONGER than this
   * for each response, so the leader always answers — with a result or its own
   * timeout — before the tab gives up on a call that may still be applied.
   */
  requestDeadlinesMs?: Readonly<Record<string, number>>;
  type: "connected";
}

/** tab → leader: invoke an allowlisted method. */
export interface RpcCallMessage {
  args: readonly unknown[];
  callId: string;
  leaderEpoch: number;
  method: string;
  type: "rpc-call";
}

/** leader → tab: success. */
export interface RpcReturnMessage {
  callId: string;
  result: unknown;
  type: "rpc-return";
}

/** leader → tab: failure (a real resource error, not a transport one). */
export interface RpcErrorMessage {
  callId: string;
  error: SerializedLeaderError;
  type: "rpc-error";
}

export type LeaderTabMessage =
  | ConnectedMessage
  | RpcCallMessage
  | RpcErrorMessage
  | RpcReturnMessage;

export type LeaderMessage = LeaderBroadcastMessage | LeaderTabMessage | LeaderWorkerMessage;

/** Every `type` this protocol recognizes. The single source of truth for `parseLeaderMessage`. */
const LEADER_MESSAGE_TYPES = new Set<string>([
  "connected",
  "event",
  "host-init",
  "host-ready",
  "leader-failed",
  "leader-failed-direct",
  "leader-here",
  "leader-now",
  "leader-paused",
  "leader-resumed",
  "relinquish",
  "relinquished",
  "rpc-call",
  "rpc-error",
  "rpc-return",
  "tab-here",
  "worker-here",
]);

function getType(value: unknown): string | null {
  if (!isAnyObject(value)) {
    return null;
  }
  const type: unknown = Reflect.get(value, "type");
  return typeof type === "string" ? type : null;
}

function hasStringField(value: object, field: string): boolean {
  return typeof Reflect.get(value, field) === "string";
}

function hasNumberField(value: object, field: string): boolean {
  return typeof Reflect.get(value, field) === "number";
}

export function isWorkerHereMessage(value: unknown): value is WorkerHereMessage {
  return getType(value) === "worker-here";
}

export function isRelinquishedMessage(value: unknown): value is RelinquishedMessage {
  return getType(value) === "relinquished";
}

export function isHostInitMessage(value: unknown): value is HostInitMessage {
  return (
    getType(value) === "host-init" &&
    isAnyObject(value) &&
    hasStringField(value, "resourceId") &&
    hasStringField(value, "tabId")
  );
}

export function isHostReadyMessage(value: unknown): value is HostReadyMessage {
  return (
    getType(value) === "host-ready" && isAnyObject(value) && hasStringField(value, "resourceId")
  );
}

export function isLeaderNowMessage(value: unknown): value is LeaderNowMessage {
  return getType(value) === "leader-now";
}

export function isLeaderFailedDirectMessage(value: unknown): value is LeaderFailedDirectMessage {
  return (
    getType(value) === "leader-failed-direct" &&
    isAnyObject(value) &&
    hasStringField(value, "reason")
  );
}

export function isRelinquishMessage(value: unknown): value is RelinquishMessage {
  return getType(value) === "relinquish";
}

export function isTabHereMessage(value: unknown): value is TabHereMessage {
  return getType(value) === "tab-here" && isAnyObject(value) && hasStringField(value, "tabId");
}

export function isLeaderHereMessage(value: unknown): value is LeaderHereMessage {
  return (
    getType(value) === "leader-here" &&
    isAnyObject(value) &&
    hasStringField(value, "resourceId") &&
    hasNumberField(value, "leaderEpoch")
  );
}

export function isLeaderFailedMessage(value: unknown): value is LeaderFailedMessage {
  return (
    getType(value) === "leader-failed" &&
    isAnyObject(value) &&
    hasStringField(value, "reason") &&
    hasNumberField(value, "leaderEpoch")
  );
}

export function isLeaderPausedMessage(value: unknown): value is LeaderPausedMessage {
  return (
    getType(value) === "leader-paused" &&
    isAnyObject(value) &&
    hasStringField(value, "reason") &&
    hasNumberField(value, "leaderEpoch")
  );
}

export function isLeaderResumedMessage(value: unknown): value is LeaderResumedMessage {
  return (
    getType(value) === "leader-resumed" &&
    isAnyObject(value) &&
    hasNumberField(value, "leaderEpoch")
  );
}

export function isLeaderEventMessage(value: unknown): value is LeaderEventMessage {
  if (getType(value) !== "event" || !isAnyObject(value)) {
    return false;
  }
  const event: unknown = Reflect.get(value, "event");
  return isAnyObject(event) && hasStringField(event, "kind");
}

/**
 * The `requestDeadlinesMs` a `connected` message carries, keeping only finite
 * positive numbers. A malformed map is ignored (the tab keeps its default budget)
 * rather than rejecting the whole handshake.
 */
export function readRequestDeadlinesMs(message: ConnectedMessage): Record<string, number> {
  const raw: unknown = Reflect.get(message, "requestDeadlinesMs");
  if (!isAnyObject(raw)) {
    return {};
  }
  const deadlines: Record<string, number> = {};
  for (const [method, value] of Object.entries(raw)) {
    if (typeof value === "number" && Number.isFinite(value) && value > 0) {
      deadlines[method] = value;
    }
  }
  return deadlines;
}

export function isConnectedMessage(value: unknown): value is ConnectedMessage {
  return (
    getType(value) === "connected" && isAnyObject(value) && hasNumberField(value, "leaderEpoch")
  );
}

export function isRpcCallMessage(value: unknown): value is RpcCallMessage {
  return (
    getType(value) === "rpc-call" &&
    isAnyObject(value) &&
    hasStringField(value, "callId") &&
    hasStringField(value, "method") &&
    hasNumberField(value, "leaderEpoch") &&
    Array.isArray(Reflect.get(value, "args"))
  );
}

export function isRpcReturnMessage(value: unknown): value is RpcReturnMessage {
  return getType(value) === "rpc-return" && isAnyObject(value) && hasStringField(value, "callId");
}

export function isRpcErrorMessage(value: unknown): value is RpcErrorMessage {
  if (getType(value) !== "rpc-error" || !isAnyObject(value) || !hasStringField(value, "callId")) {
    return false;
  }
  const error: unknown = Reflect.get(value, "error");
  return isAnyObject(error) && hasStringField(error, "name") && hasStringField(error, "message");
}

/**
 * Narrow an arbitrary `MessageEvent.data` to a known protocol message, or `null`.
 *
 * Returns `null` for every non-message — other libraries share the same
 * BroadcastChannel namespace only by accident, but a devtools bridge or an
 * extension can post anything onto a `Worker`'s message port, so unrecognized
 * shapes must be ignored rather than crash a handler.
 */
export function parseLeaderMessage(value: unknown): LeaderMessage | null {
  const type = getType(value);
  if (type === null || !LEADER_MESSAGE_TYPES.has(type)) {
    return null;
  }
  if (
    isWorkerHereMessage(value) ||
    isHostInitMessage(value) ||
    isHostReadyMessage(value) ||
    isLeaderNowMessage(value) ||
    isLeaderFailedDirectMessage(value) ||
    isRelinquishMessage(value) ||
    isTabHereMessage(value) ||
    isLeaderHereMessage(value) ||
    isLeaderFailedMessage(value) ||
    isLeaderPausedMessage(value) ||
    isLeaderResumedMessage(value) ||
    isLeaderEventMessage(value) ||
    isConnectedMessage(value) ||
    isRpcCallMessage(value) ||
    isRpcReturnMessage(value) ||
    isRpcErrorMessage(value)
  ) {
    return value;
  }
  return null;
}

/** Serialize an error for the wire, preserving the `code` `structuredClone` would drop. */
export function serializeLeaderError(error: unknown): SerializedLeaderError {
  if (error instanceof Error) {
    const code: unknown = Reflect.get(error, "code");
    return {
      ...(typeof code === "string" ? { code } : {}),
      message: error.message,
      name: error.name,
      ...(error.stack === undefined ? {} : { stack: error.stack }),
    };
  }
  return { message: String(error), name: "Error" };
}

/** Rebuild an `Error` from the wire, restoring `name`, `stack`, and `code`. */
export function deserializeLeaderError(serialized: SerializedLeaderError): Error {
  const error = new Error(serialized.message);
  error.name = serialized.name;
  if (serialized.stack !== undefined) {
    error.stack = serialized.stack;
  }
  if (serialized.code !== undefined) {
    // `code` is what `isDatabaseLockedError` classifies on; losing it silently
    // degrades busy-retry to message-matching only.
    Reflect.set(error, "code", serialized.code);
  }
  return error;
}
